import { useEffect } from "react";
import { useSelector } from "react-redux";
import { getAccessToken } from "../Util/authStorage.js";
import { doLogout, isDefiniteAuthFailure, silentRefresh } from "../axios.js";

// Refresh this many ms BEFORE the access token's `exp`, so a fresh token is
// always in hand before the old one lapses. The access token TTL is 15 min.
const REFRESH_SKEW_MS = 90 * 1000;
// Never schedule a timer shorter than this (avoids a tight loop if exp is odd).
const MIN_DELAY_MS = 5 * 1000;

const decodeClaims = (token) => {
    try {
        return JSON.parse(atob(token.split(".")[1]));
    } catch {
        return null;
    }
};

/**
 * Keeps the in-memory access token fresh while the user is logged in:
 *
 *  1. On mount (e.g. right after a page reload the access token is null because
 *     it lives in memory only) it silently mints one from the refresh cookie —
 *     so the first protected API call no longer has to 401-then-refresh.
 *  2. Then it schedules a refresh ~90s before each token expires, rescheduling
 *     off every newly minted token.
 *
 * The server caps a token's exp at the absolute session deadline (sexp). A
 * capped token is the session's last one — refreshing it can't extend
 * anything — so no refresh is scheduled for it.
 *
 * A definite auth rejection (400/401) means the session is over → log out.
 * Transient failures (network / 5xx) are swallowed.
 * Runs only when a user is present in Redux, so it never hammers /login/refresh
 * on the login screen.
 */
const useProactiveRefresh = () => {
    const user = useSelector((s) => s.auth?.user);

    useEffect(() => {
        if (!user) return undefined;

        let timer = null;
        let cancelled = false;

        const scheduleFrom = (token) => {
            const claims = decodeClaims(token);
            if (!claims?.exp) return;
            if (claims.sexp && claims.exp >= claims.sexp) return; // last token of the session
            const expMs = claims.exp * 1000;
            const delay = Math.max(MIN_DELAY_MS, expMs - Date.now() - REFRESH_SKEW_MS);
            timer = setTimeout(tick, delay);
        };

        const tick = async () => {
            if (cancelled) return;
            try {
                const token = await silentRefresh();
                if (!cancelled) scheduleFrom(token);
            } catch (err) {
                if (!cancelled && isDefiniteAuthFailure(err)) doLogout();
            }
        };

        (async () => {
            let token = getAccessToken();
            if (!token) {
                try {
                    token = await silentRefresh();
                } catch (err) {
                    if (!cancelled && isDefiniteAuthFailure(err)) doLogout();
                    return;
                }
            }
            if (!cancelled) scheduleFrom(token);
        })();

        return () => {
            cancelled = true;
            if (timer) clearTimeout(timer);
        };
    }, [user]);
};

export default useProactiveRefresh;
